import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import RichTextEditor from '@/modules/corporate/modules/questions/fields/components/Editor/RichTextEditor';
import {
  correctOptionItemBorder,
  optionCodeStyles,
} from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/styles';
import useEditorOptionsListItem from '@/modules/corporate/modules/questions/fields/hooks/useEditorOptionsListItem';

import { renderDeleteAction, renderOptionKeyCode } from './renderers';
import { OptionListItemProps } from './types';

const OptionsListItem = (props: OptionListItemProps) => {
  const {
    viewMode,
    classes,
    option,
    index,
    isDeletable = viewMode !== 'view',
    isEditable = viewMode !== 'view',
    onAction,
    showEvaluation = true,
    showWeight = false,
    isLocalizedContent,
  } = props;

  const { t } = useTranslation();
  const { onToggleEvaluation, onRemove, onChangeText, onChangeWeight } = useEditorOptionsListItem({
    option,
    index,
    onAction,
  });

  const isViewMode = viewMode === 'view';
  const isEditingDisabled = viewMode !== 'create';

  return (
    <Stack
      alignItems="start"
      aria-label={option.optionEvaluation ? 'correct answer' : undefined}
      className={cn(
        'w-full rounded-8 border bg-bg-secondary py-8',
        !isEditable
          ? 'disabled'
          : 'focus-within:border-interactive-primary hover:border-border-dark',
        option.optionEvaluation ? correctOptionItemBorder : 'border-border',
        classes?.root,
      )}>
      {showEvaluation
        ? renderOptionKeyCode({
            t,
            optionEvaluation: option.optionEvaluation,
            onClick: onToggleEvaluation,
            disabled: isViewMode || showWeight,
            isLocalizedContent,
          })
        : null}
      <Stack alignItems="center" className="flex-1 gap-12" direction="row">
        {isEditable ? (
          <RichTextEditor
            className={cn('flex-1', optionCodeStyles(), classes?.optionItem)}
            placeholder={t('QuizCreator:Option', { index: index + 1 })}
            value={option.optionText}
            onChangeText={onChangeText}
          />
        ) : (
          <RichTextViewer className={cn('flex-1', classes?.optionItem)} html={option.optionText} />
        )}
        {showWeight && isEditable && (
          <input
            className="h-40 w-80 rounded-6 border border-border bg-white px-8 py-4 text-center text-14 font-medium text-text-primary placeholder-text-tertiary focus:border-interactive-primary focus:outline-none"
            max="100"
            min="0"
            placeholder="%"
            type="number"
            value={option.optionRelativeScore ?? 0}
            onChange={(e) => {
              const value = Math.min(100, Math.max(0, Number(e.target.value) || 0));
              onChangeWeight(value);
            }}
          />
        )}
        {showWeight && !isEditable && (
          <div className="w-80 text-center text-14 font-medium text-text-secondary">
            {option.optionRelativeScore ?? 0} %
          </div>
        )}
      </Stack>
      {isDeletable && !isLocalizedContent
        ? renderDeleteAction({ onRemove, disabled: isEditingDisabled })
        : null}
    </Stack>
  );
};

export default OptionsListItem;
